import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, OAuthProvider, RecaptchaVerifier, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref, set, get } from "firebase/database";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { useToastStore } from "store/toastStore";
import { getFirestore, doc, setDoc, addDoc, collection, getDocs, getDoc, onSnapshot } from "firebase/firestore";
import { Timestamp } from "firebase-admin/firestore";
import firebase from 'firebase/app';
import { writeBatch } from "firebase/firestore";
import firebaseProjects from "./firebaseProjects.json";


interface Notification {
  id: string;
  IsRead: boolean;
  createdAt: Timestamp;
  message: string;
  requester: string;
  responder: string;
  title: string;
}




// Firebase web SDK configuration. Both projects' configs live in
// firebaseProjects.json (checked in — these values are NOT secrets; Firebase
// web config ships in the client bundle and access is gated by Security
// Rules). REACT_APP_FIREBASE_ENV selects the Firebase project independently
// of REACT_APP_ENV (needed for .env.qaproduction, which builds in production
// mode but must still point at QA Firebase); it falls back to REACT_APP_ENV
// when unset. Only an explicit "production" build (.env.production) picks the
// production Firebase project — every other case (local dev, QA, qaproduction)
// picks QA. Keeping the configs in a checked-in file (not per-developer .env
// vars) means a mis-set env var can't silently point the app at a wrong project.
const firebaseEnv = process.env.REACT_APP_FIREBASE_ENV || process.env.REACT_APP_ENV;
const firebaseProject =
  firebaseEnv === "production" ? firebaseProjects.prod : firebaseProjects.qa;
const firebaseConfig = firebaseProject.config;

// Cloud Messaging Web Push certificate public key for the active project.
// Empty string means push tokens can't be requested in this environment yet.
const FIREBASE_VAPID_KEY = firebaseProject.vapidKey;

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Firebase Cloud Messaging requires the Service Worker API, which browsers only
// expose on localhost or HTTPS. Over a plain-HTTP LAN origin (e.g. testing on a
// phone via http://192.168.x.x:3000) `navigator.serviceWorker` is undefined and
// getMessaging() throws, crashing the whole app at module load. Guard it so the
// app still loads; push notifications simply won't be available in that context.
const isMessagingSupported =
  typeof navigator !== "undefined" && "serviceWorker" in navigator;

let messaging: ReturnType<typeof getMessaging> | null = null;
if (isMessagingSupported) {
  try {
    messaging = getMessaging(app);
  } catch (err) {
    console.warn("Firebase Messaging is not available in this context:", err);
  }
}
const auth = getAuth(app);
const database = getDatabase(app);
const firestore = getFirestore(app);
const appleProvider = new OAuthProvider("apple.com");
// Force Apple's sign-in page to English (otherwise it follows the browser/device language)
appleProvider.setCustomParameters({ locale: "en" });
const googleProvider = new GoogleAuthProvider();

// ✅ Function to Get the Current User's UID
const getUserId = (): Promise<string | null> => {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        resolve(user.uid);
      } else {
        resolve(null);
      }
    });
  });
};

// ✅ Function to Save User Settings
const saveSettings = (userId: string, settings: object) => {
  const settingsRef = ref(database, 'users/' + userId + '/settings');

  return new Promise<void>((resolve, reject) => {
    set(settingsRef, settings)
      .then(() => {
        resolve(); // Resolve when saved successfully
      })
      .catch((error) => {
        reject(error); // Reject on error
      });
  });
};

// ✅ Function to Get User Settings
const getSettings = async (userId: string) => {
  const settingsRef = ref(database, 'users/' + userId + '/settings');
  const snapshot = await get(settingsRef);
  if (snapshot.exists()) {
    return snapshot.val();
  } else {
    return null; // No settings found
  }
};

// Function to request notification permission and get the device token
const logDeviceToken = async () => {
  try {
    if (!messaging) {
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return null;
    }

    if (!FIREBASE_VAPID_KEY) {
      console.warn("No VAPID key configured for this environment; skipping push token.");
      return null;
    }
    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
    });

    // Display token on screen if needed
    const displayElement = document.getElementById("token-display");
    if (displayElement) {
      displayElement.innerText = token;
    }

    // Save token to Firestore if user is authenticated
    const user = auth.currentUser;
    if (user && token) {
      const tokenRef = doc(firestore, "deviceTokens", user.uid);
      await setDoc(tokenRef, { token });
    }

    return token;
  } catch (err) {
    console.error("Error getting or saving device token:", err);
    return null;
  }
};


// ✅ New function to handle FCM messages with toast notifications
const setupMessageHandler = () => {
  if (!messaging) {
    return () => {}; // no-op unsubscribe
  }

  const { showToast } = useToastStore.getState(); // Get the function directly from the store

  return onMessage(messaging, (payload) => {
    // Extract notification data
    const title = payload.notification?.title || 'New Notification';
    const message = payload.notification?.body || '';

    // Determine notification type based on payload if available
    let type = "info";
    if (payload.data && payload.data.type) {
      if (payload.data.type === "success" || payload.data.type === "error") {
        type = payload.data.type;
      }
    }

    // Show toast notification using your Zustand store
    showToast(message, type as "success" | "error" | "info", 5000);
  });
};


export const requestPermissionAndSaveToken = async () => {
  try {
    if (!messaging) {
      return;
    }
    if (!FIREBASE_VAPID_KEY) {
      console.warn("No VAPID key configured for this environment; skipping push token.");
      return;
    }
    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
    });

    if (token) {
      const user = auth.currentUser;
      if (!user) return;

      // Save using UID as document ID to avoid duplicates
      await setDoc(doc(firestore, "deviceTokens", user.uid), {
        uid: user.uid,
        token: token,
        updatedAt: new Date(),
      });
    } else {
      console.warn("⚠️ No token received, permission might be denied.");
    }
  } catch (error) {
    console.error("❌ Error getting FCM token or saving to Firestore:", error);
  }
};




const fetchUserNotifications = async (): Promise<Notification[] | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    const uid = user.uid;

    const notificationsCollectionRef = collection(
      firestore,
      "load_notifications",
      uid,
      "notifications"
    );

    const querySnapshot = await getDocs(notificationsCollectionRef);

    if (!querySnapshot.empty) {
      const notifications: Notification[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          IsRead: data.IsRead,
          createdAt: data.createdAt,
          message: data.message,
          requester: data.requester,
          responder: data.responder,
          responderName: data.responderName,
          title: data.title,
        };
      });

      return notifications;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};


export const updateNotificationAsRead = async (
  notificationId: string,
  userId: string,
  collectionName: "load_notifications" | "admin_notifications" = "load_notifications"
) => {
  try {
    // Create the reference to the specific notification document by its ID
    const docRef = doc(firestore, collectionName, userId, 'notifications', notificationId);

    // Get the document snapshot
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { success: false, count: 0 };
    }

    // Prepare a batch for updating notifications
    const batch = writeBatch(firestore);

    // Update the IsRead field for the specific notification document
    batch.update(docRef, { IsRead: true });

    // Commit the batch update
    await batch.commit();

    return { success: true, count: 1 };
  } catch (error) {
    console.error("Error updating notification:", error);
    return { success: false, error };
  }
};

export const subscribeToUserNotifications = (
  userId: string,
  callback: (notifications: any[]) => void,
  collectionName: "load_notifications" | "admin_notifications" = "load_notifications"
) => {
  const notificationsRef = collection(firestore, collectionName, userId, "notifications");

  // No orderBy("createdAt") here: Firestore treats orderBy as a filter and
  // silently drops any document that is missing that field, which can hide
  // notifications entirely. Consumers sort client-side, so order it there.
  return onSnapshot(
    notificationsRef,
    (snapshot) => {
      const notifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(notifications);
    },
    (error) => {
      // Surface permission/index errors instead of failing silently.
      console.error("Error subscribing to notifications:", error);
      callback([]);
    },
  );
};

// Export everything
export {
  app,
  // analytics,
  auth,
  database,
  GoogleAuthProvider,
  googleProvider,
  appleProvider,
  signInWithPopup,
  RecaptchaVerifier,
  getUserId,
  messaging,
  saveSettings,
  getSettings,
  logDeviceToken,
  firestore,
  setupMessageHandler, // Export the new function
  fetchUserNotifications
};